<template>
  <v-progress-linear v-if="loading" indeterminate />
  <div v-else>
    <span>Permissions set here affect all members of this group.</span>
    <div>
      <div v-for="(type, index) in allPermissionTypes" :key="index">
        <v-divider class="mt-2 bg-primary" />
        <h2 class="mt-4">
          {{ type.charAt(0).toUpperCase() + type.slice(1) }}
        </h2>
        <v-list>
          <div
            v-for="(permission, index) in allPermissionsOptions.filter(
              permission => permission.type === type
            )"
            :key="index"
          >
            <v-divider v-if="Number(index) !== 0" />
            <v-list-item class="ma-2">
              <v-list-item-title
                >{{ formatName(permission.name) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ permission.description }}
              </v-list-item-subtitle>

              <template #append>
                <v-tooltip
                  :disabled="
                    !permission.dependencies ||
                    permission.dependencies.some(dep =>
                      groupPermissionNames.includes(dep)
                    )
                  "
                  location="start"
                >
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-switch
                        :model-value="
                          groupPermissionNames.includes(permission.name)
                        "
                        @update:model-value="
                          updatePermission(permission, $event)
                        "
                        :disabled="
                          permission.dependencies &&
                          !permission.dependencies.some(dep =>
                            groupPermissionNames.includes(dep)
                          )
                        "
                        class="test-switch"
                      >
                      </v-switch>
                    </div>
                  </template>
                  <span v-if="permission.dependencies"
                    >To enable, one or more of the following permission(s) must
                    be enabled:
                    {{ formatDependencies(permission.dependencies) }}</span
                  >
                </v-tooltip>
              </template>
            </v-list-item>
          </div>
        </v-list>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, Ref, computed } from 'vue';
import {
  PermissionItem,
  UpdatePermissionPayload,
  PermissionName,
} from '@oare/types';
import sl from '@/serviceLocator';

export default defineComponent({
  props: {
    groupId: {
      type: String,
      required: true,
    },
  },
  setup({ groupId }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const loading = ref(false);

    const allPermissionsOptions: Ref<PermissionItem[]> = ref([]);

    const allPermissionTypes = computed(() => {
      const types = allPermissionsOptions.value.map(
        permission => permission.type
      );
      return [...new Set(types)];
    });

    const groupPermissions: Ref<PermissionItem[]> = ref([]);

    const groupPermissionNames: Ref<PermissionName[]> = computed(() => {
      return groupPermissions.value.map(permission => permission.name);
    });

    const formatName = (name: string) => {
      const lowerCase = name.replace(/_/g, ' ').toLowerCase();
      return lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1);
    };

    const formatDependencies = (permissions: PermissionName[]) => {
      let list = '';
      permissions.forEach((perm, idx) => {
        list += formatName(perm);
        if (idx < permissions.length - 1) {
          list += ', ';
        }
      });
      return list;
    };

    const updatePermission = async (
      permission: PermissionItem,
      isAllowed: boolean | null
    ) => {
      try {
        if (isAllowed) {
          const payload: UpdatePermissionPayload = {
            permission,
          };
          await server.addGroupPermission(groupId, payload);
        } else {
          await server.removePermission(groupId, permission.name);
        }
        groupPermissions.value = await server.getGroupPermissions(
          Number(groupId)
        );
      } catch (err) {
        actions.showErrorSnackbar(
          'Error updating permission. Please try again.',
          err as Error
        );
      }
    };

    onMounted(async () => {
      try {
        loading.value = true;

        groupPermissions.value = await server.getGroupPermissions(
          Number(groupId)
        );
        allPermissionsOptions.value = await server.getAllPermissions();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading group permissions. Please try again.',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      allPermissionsOptions,
      allPermissionTypes,
      groupPermissions,
      groupPermissionNames,
      formatName,
      updatePermission,
      formatDependencies,
    };
  },
});
</script>
